import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { COLORS, cardSx } from '../theme';
import WidgetSkeleton from './WidgetSkeleton';

interface IProps {
  title: string;
  /**
   * Sits immediately after the title. `headerAction` renders at the far right
   * of the header, which on a half-width card in the two-column grid can end up
   * outside the viewport — a help icon nobody can find isn't help (FB-003).
   */
  titleAdornment?: ReactNode;
  headerAction?: ReactNode;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  children: ReactNode;
}

const WidgetCard = ({ title, titleAdornment, headerAction, loading, error, isEmpty, children }: IProps) => (
  // rv2-card is the print hook: emotion class names are hashed, so the print
  // stylesheet has no stable selector to keep a card whole across a page break.
  <Box className='rv2-card' sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
    {(title || titleAdornment || headerAction) && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
            {title}
          </Typography>
          {titleAdornment}
        </Box>
        {headerAction}
      </Box>
    )}
    {loading ? (
      <WidgetSkeleton />
    ) : error ? (
      <Typography variant='body2' color='error'>
        {error}
      </Typography>
    ) : isEmpty ? (
      <Typography variant='body2' color='text.secondary'>
        No data for the selected range yet.
      </Typography>
    ) : (
      children
    )}
  </Box>
);

export default WidgetCard;
